import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { A } from '..';
import styles from './InputFile.module.scss';

export interface iInputFile {
  name: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  multiple?: boolean;
  error?: React.ReactNode;
  success?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileChange?: (e: FileList | undefined | null) => void;
  onRemove?: () => void;
  loading?: boolean;
  information?: React.ReactNode;
  fileName?: string;
  accept?: string;
  onlyButton?: boolean;
  buttonLabel?: string;
  noFileSelected?: string;
}

const InputFile = React.forwardRef(function field(
  {
    name,
    id,
    label,
    disabled,
    multiple,
    loading,
    onChange,
    onFileChange,
    onRemove,
    fileName,
    error,
    success,
    information,
    required,
    accept = 'image/png, image/jpeg, image/jpg',
    onlyButton,
    buttonLabel = 'Choose file',
    noFileSelected = 'No file selected',
  }: iInputFile,
  outerRef: React.ForwardedRef<HTMLInputElement>
) {
  const [dragging, setDragging] = React.useState(false);
  const innerRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(outerRef, () => innerRef.current!, []);

  useEffect(() => {
    if (!labelRef.current) return;

    labelRef.current.addEventListener(
      'dragenter',
      function (evt) {
        evt.preventDefault();
        setDragging(true);
      },
      false
    );

    labelRef.current.addEventListener(
      'dragover',
      function (evt) {
        evt.preventDefault();
        setDragging(true);
      },
      false
    );

    labelRef.current.addEventListener(
      'dragleave',
      function (evt) {
        evt.preventDefault();
        setDragging(false);
      },
      false
    );

    labelRef.current.addEventListener(
      'drop',
      function (evt) {
        evt.preventDefault();
        setDragging(false);

        const dt = evt.dataTransfer;
        const files = dt?.files;

        if (onFileChange) onFileChange(files);
      },
      false
    );
  }, [disabled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onFileChange) onFileChange(e.target.files);
    if (onChange) onChange(e);
  };

  return (
    <div className={styles.InputFile}>
      {label && (
        <label
          htmlFor={id ?? name}
          className={styles.InputFile__label}
          data-error={!!error}
          data-disabled={disabled}
        >
          {label}
          {required && <i data-asterix>*</i>}
        </label>
      )}
      {information && (
        <A.Text
          as="div"
          mb="10"
          mt="-3"
          fontSize="14"
          color={disabled ? 'grey-20' : 'grey-50'}
        >
          {information}
        </A.Text>
      )}
      <label
        htmlFor={id ?? name}
        className={styles.InputFile__group}
        data-error={!!error}
        data-disabled={disabled}
        data-only-button={!!onlyButton}
        ref={labelRef}
        data-dragging={dragging}
      >
        {!onlyButton && (
          <div
            className={styles.InputFile__fileName}
            data-placeholder={!fileName}
            data-error={!!error}
            data-disabled={disabled}
          >
            {fileName ?? noFileSelected}
          </div>
        )}
        <input
          name={name}
          type="file"
          id={id ?? name}
          disabled={disabled}
          multiple={multiple}
          onChange={handleChange}
          accept={accept}
          className={styles.InputFile__field}
          ref={innerRef}
        />
        {fileName && (
          <div
            className={styles.InputFile__trash}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onRemove) onRemove();
            }}
          >
            <A.Icon icon="trash" />
          </div>
        )}
        <div
          className={styles.InputFile__button}
          data-button-disabled={disabled}
        >
          {loading ? <A.Loading color="white" width={20} /> : buttonLabel}
        </div>
      </label>

      <A.Text as="div" mt="5" fontSize="14" color="success">
        {success}
      </A.Text>
      <A.Text as="div" mt="5" fontSize="14" color="alert">
        {error}
      </A.Text>
    </div>
  );
});

InputFile.defaultProps = {
  required: undefined,
  multiple: false,
  disabled: undefined,
};

export { InputFile };
